// src/context/MessagesContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLanguage } from "./LanguageContext";

const MessagesContext = createContext();
const MessagesStatusContext = createContext({ lang: null, error: false });

// Spring incrusta en el HTML inicial las traducciones del idioma de la URL; así el primer
// render no depende de /api/messages. Solo se usan si corresponden al idioma pedido.
export const INLINE_MESSAGES_ID = "hidromed-messages";

function readInlineMessages(lang) {
  if (typeof document === "undefined") return null;

  const element = document.getElementById(INLINE_MESSAGES_ID);
  if (!element) return null;

  try {
    const data = JSON.parse(element.textContent);
    return data && data.lang === lang && data.messages ? data.messages : null;
  } catch (e) {
    return null;
  }
}

export const MessagesProvider = ({ children }) => {
  const { lang } = useLanguage();
  const [labels, setLabels] = useState(() => readInlineMessages(lang) || {});
  // Idioma al que pertenecen los labels actuales (null mientras no hay ninguno cargado)
  const [status, setStatus] = useState(() =>
    readInlineMessages(lang) ? { lang, error: false } : { lang: null, error: false }
  );
  // Idioma de los labels que hay en pantalla (puede diferir de status.lang si falló otro idioma)
  const labelsLangRef = useRef(status.lang);

  useEffect(() => {
    let cancelled = false;

    const showLabels = (messages) => {
      labelsLangRef.current = lang;
      setLabels(messages);
      setStatus({ lang, error: false });
    };

    // Los labels de este idioma ya están cargados (p. ej. incrustados en el HTML inicial)
    if (labelsLangRef.current === lang) {
      setStatus((prev) => (prev.lang === lang && !prev.error ? prev : { lang, error: false }));
      return;
    }

    const inline = readInlineMessages(lang);
    if (inline) {
      showLabels(inline);
      return;
    }

    const storageKey = `messages_${lang}`;
    const versionKey = `messages_version_${lang}`;

    // versión esperada (puede venir hardcodeada o del backend)
    // Cambiar version
    const currentVersion = "2.1.8";

    const cached = localStorage.getItem(storageKey);
    const cachedVersion = localStorage.getItem(versionKey);

    if (cached && cachedVersion === currentVersion) {
      // ✅ usa cache si coincide la versión
      showLabels(JSON.parse(cached));
    } else {
      // 🔄 pide al backend; ?lang= separa la URL por idioma para que ningún caché mezcle respuestas
      fetch(`/api/messages?lang=${encodeURIComponent(lang)}`, { headers: { "Accept-Language": lang } })
        .then((res) => res.json())
        .then((data) => {
          // ignora respuestas de un idioma anterior que lleguen tarde
          if (cancelled) return;
          showLabels(data.messages);
          localStorage.setItem(storageKey, JSON.stringify(data.messages));
          localStorage.setItem(versionKey, data.version);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("Error loading messages:", err);
          setStatus({ lang, error: true });
        });
    }

    return () => {
      cancelled = true;
    };
  }, [lang]);

  return (
    <MessagesStatusContext.Provider value={status}>
      <MessagesContext.Provider value={labels}>
        {children}
      </MessagesContext.Provider>
    </MessagesStatusContext.Provider>
  );
};

export const useMessages = () => useContext(MessagesContext);

// { lang, error }: lang es el idioma de los labels cargados; error indica que falló la carga de ese idioma
export const useMessagesStatus = () => useContext(MessagesStatusContext);
